import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import useAlert from '../alert';
import localStorage from '../../utils/localStorage';
import useWeb3 from '../web3';
import { minifyHash } from '../../utils/utils';
import fetcher, { Fetcher } from '../../utils/fetcher';
import InvalidTokenBlock from './invalidToken';
import { useAssets } from '../assets';

type Login = (url?: string) => void;
type Logout = (resetTokenCheck?: boolean) => void;

interface IUserData {
  loggedIn: boolean;
  login: Login;
  logout: Logout;
  userName: string;
  userAddress: string;
  userDataLoading: boolean;
  loginLoading: boolean;
  logoutLoading: boolean;
}

// @ts-ignore
const UserDataContext = createContext<IUserData>();

interface ProviderProps {
  children: React.ReactNode;
}

export const UserDataContextProvider = ({ children }: ProviderProps) => {
  const alert = useAlert();
  const navigate = useNavigate();
  const { init: initWeb3, connect, getWeb3, openInstallDialog } = useWeb3();
  const { setAppLoading } = useAssets();

  const [loggedIn, setLoggedIn] = useState(false);
  const [invalidToken, setInvalidToken] = useState(false);

  const [address, setAddress] = useState<string>('');

  //LOADERS
  const [tokenCheckLoading, setTokenCheckLoading] = useState(true);
  const [loginLoading, setLoginLoading] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);

  //validating token with our BE
  useEffect(() => {
    initWeb3().then(() => {
      const cachedAddress = localStorage.getAddress();
      if (cachedAddress) setAddress(cachedAddress);
      const token = localStorage.getToken();
      if (token) {
        validateToken(token);
      } else {
        setTokenCheckLoading(false);
      }
    });
  }, []);

  const validateToken = (token: string, stopLoading?: () => any) => {
    fetcher.user
      .validateToken(token)
      .then(() => {
        Fetcher.setTokenHeader(token);
        //giving some time for axios to apply default header
        setTimeout(() => {
          setInvalidToken(false);
          setLoggedIn(true);
          setTokenCheckLoading(false);
          if (stopLoading) stopLoading();
        }, 500);
      })
      .catch(() => {
        setInvalidToken(true);
        setLoggedIn(false);
        setTokenCheckLoading(false);
        if (stopLoading) stopLoading();
        setAppLoading(false);
      });
  };

  //Building user name
  const userName = useMemo<string>(() => {
    const localUserId = address ? address : localStorage.getAddress();
    if (localUserId) return minifyHash(localUserId);
    return '';
  }, [address]);

  const login = (url?: string) => {
    connect(false)
      .then(({ publicAddress }) => {
        checkAddress(publicAddress, url);
      })
      .catch((err) => {
        if (err.type === 'install') {
          openInstallDialog();
        } else if (err.type === 'common') {
          alert.showError(err.message);
        }
        setLoginLoading(false);
      });
  };

  const checkAddress = (publicAddress: string, redirect?: string): void => {
    fetcher.user
      .checkAddress(publicAddress)
      .then((response) => {
        signMessage(publicAddress, response.nonce, redirect);
      })
      .catch((err) => {
        alert.showError(err);
        setLoginLoading(false);
      });
  };

  const signMessage = async (
    publicAddress: string,
    nonce: string,
    redirect?: string
  ): Promise<void> => {
    try {
      const web3 = getWeb3();
      const signature = await web3.eth.personal.sign(
        `Nifty Hands authentication: ${nonce}`,
        publicAddress,
        ''
      );
      completeLogin(publicAddress, signature, redirect);
    } catch (err) {
      alert.showError('You need to sign a message to complete login');
      setLoginLoading(false);
    }
  };

  const completeLogin = (
    publicAddress: string,
    signature: string,
    redirect?: string
  ) => {
    fetcher.user
      .login(publicAddress, signature)
      .then((response) => {
        Fetcher.setTokenHeader(response.token);
        localStorage.setAddress(publicAddress);
        localStorage.setToken(response.token);
        setTimeout(() => {
          setLoggedIn(true);
          setLoginLoading(false);
          setAddress(publicAddress);
          navigate(redirect || '/');
        }, 500);
      })
      .catch((err) => {
        alert.showError(err);
        setLoginLoading(false);
        localStorage.clear();
      });
  };

  const logout = (resetTokenCheck = false) => {
    setLogoutLoading(true);
    fetcher.user
      .logout()
      .catch(() => {
        /*Do nothing*/
      })
      .finally(() => {
        setLoggedIn(false);
        setLogoutLoading(false);
        Fetcher.clearHeaders();
        localStorage.clear();
        if (resetTokenCheck) setInvalidToken(false);
        navigate('/');
      });
  };

  return (
    <UserDataContext.Provider
      value={{
        //AUTH
        loggedIn,
        login,
        logout,
        userName,
        userAddress: address,

        //LOADERS
        userDataLoading: tokenCheckLoading,
        loginLoading,
        logoutLoading,
      }}
    >
      {invalidToken ? (
        <InvalidTokenBlock
          logout={() => logout(true)}
          verifyId={(stopLoading) =>
            validateToken(localStorage.getToken(), stopLoading)
          }
          logoutLoading={logoutLoading}
        />
      ) : (
        children
      )}
    </UserDataContext.Provider>
  );
};

const useUserData = () => useContext(UserDataContext);

export default useUserData;
